var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef } from 'react';
import styled, { css } from '../../styled';
import * as Tooltip from '../tooltip';
import Text from '../text';
import { Cross } from '../icons/outline/Cross';
import { ExclamationMark } from '../icons/outline/ExclamationMark';
import LoadingSpinner from '../loadingSpinner';
import { ImageUpload } from '../imageUpload';
const StyledImageUpload = styled(ImageUpload) `
  height: 100%;
  width: 100%;
  position: absolute;
`;
const ImageUploadWrapper = styled.div `
  display: ${({ shouldShow }) => (shouldShow ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  border-radius: ${({ theme, borderRadius }) => theme.showImage.borderRadius[borderRadius]};
`;
const ImageUploadContainer = styled.div `
  text-align: center;
`;
const ExclamationMarkIconWrapper = styled.div `
  z-index: 2;
  width: 2.5rem;
  height: 2.5rem;
  position: absolute;
  right: 0;
  top: 0;
`;
const CrossIconWrapper = styled(ExclamationMarkIconWrapper) `
  :hover {
    background-color: ${({ theme }) => theme.colors.PRIMARY_A400};
  }
`;
const CrossIconInnerWrapper = styled.div `
  padding: 0.5rem;
  :hover {
    > div > div > svg {
      fill: ${({ theme }) => theme.colors.RED_600};
    }
  }
`;
const ImageIconWrapper = styled.div `
  height: 2rem;
`;
const Title = styled(Text) `
  padding-top: 0.25rem;
  display: block;
`;
const ImageWrapper = styled.div `
  ${props => props.imageSource &&
    css `
      background-image: url(${props.imageSource});
    `};
  width: 100%;
  height: 100%;
  border-radius: ${({ theme, borderRadius }) => theme.showImage.borderRadius[borderRadius]};
  background-position: center;
  background-size: cover;
`;
const generateWrapperStyle = (props) => {
    const { hasImageSource, isError, isLoading } = props;
    if (isError) {
        return css `
      border: 1px dashed ${({ theme }) => theme.colors.RED_600};
    `;
    }
    if (hasImageSource && !isLoading) {
        return css `
      :hover {
        ${ImageUploadWrapper} {
          display: flex;
          z-index: 1;
          background-color: ${({ theme }) => theme.colors.SNOW_A900};
        }
        ${Title} {
          color: ${({ theme }) => theme.colors.INK_500};
        }
      }
    `;
    }
    if (isLoading) {
        return css `
      border: 1px dashed ${({ theme }) => theme.colors.INK_300};
    `;
    }
    if (!hasImageSource && !isLoading) {
        return css `
      border: 1px dashed ${({ theme }) => theme.colors.INK_300};
      :hover {
        ${ImageUploadWrapper} {
          display: flex;
          z-index: 1;
          background-color: ${({ theme }) => theme.colors.PRIMARY_A200};
        }
        border-color: ${({ theme }) => theme.colors.PRIMARY_400};
        color: ${({ theme }) => theme.colors.PRIMARY_600};
        ${Title} {
          color: ${({ theme }) => theme.colors.PRIMARY_600};
        }
        ${ImageIconWrapper} {
          > svg {
            fill: ${({ theme }) => theme.colors.PRIMARY_600};
          }
        }
      }

      :active {
        border: none;
        color: ${({ theme }) => theme.colors.PRIMARY_500};
        ${ImageUploadWrapper} {
          background-color: ${({ theme }) => theme.colors.PRIMARY_A400};
        }
        ${Title} {
          color: ${({ theme }) => theme.colors.PRIMARY_500};
        }
        ${ImageIconWrapper} {
          > svg {
            fill: ${({ theme }) => theme.colors.PRIMARY_500};
          }
        }
      }
    `;
    }
    return null;
};
const Wrapper = styled.div `
  position: relative;
  box-sizing: border-box;
  height: ${props => `${props.height}px` || '100%'};
  width: ${props => `${props.width}px` || '100%'};
  border-radius: ${({ theme, borderRadius }) => theme.showImage.borderRadius[borderRadius]};
  cursor: ${({ isLoading }) => (isLoading ? 'not-allowed' : 'pointer')};
  ${props => generateWrapperStyle(props)}
`;
const ShowImage = (_a) => {
    var { width, height, borderRadius = 'none', Icon, onChange, onError, title, description, tooltipMessage, lang, imageSource, onRemoveClick, isError = false, errorTooltipMessage, isLoading = false, loadingText } = _a, restProps = __rest(_a, ["width", "height", "borderRadius", "Icon", "onChange", "onError", "title", "description", "tooltipMessage", "lang", "imageSource", "onRemoveClick", "isError", "errorTooltipMessage", "isLoading", "loadingText"]);
    const inputRef = useRef(null);
    const onCrossIconClick = () => {
        if (inputRef && inputRef.current) {
            // @ts-ignore
            inputRef.current.value = '';
            onRemoveClick();
        }
    };
    return (React.createElement(Wrapper, { hasImageSource: !!imageSource, width: width, height: height, borderRadius: borderRadius, isError: isError, isLoading: isLoading },
        React.createElement(StyledImageUpload, Object.assign({ onChange: onChange, onError: onError, ref: inputRef, render: (onFileInputClick) => (React.createElement(React.Fragment, null,
                React.createElement(ImageUploadWrapper, { shouldShow: !imageSource || !!(imageSource && isLoading), onClick: e => !isLoading && onFileInputClick(e), borderRadius: borderRadius },
                    React.createElement(ImageUploadContainer, null, !isLoading ? (React.createElement(React.Fragment, null,
                        React.createElement(ImageIconWrapper, null,
                            React.createElement(Icon, { size: "XXLARGE" })),
                        React.createElement(Title, { fontType: "Body", color: "INK_600", lang: lang }, title),
                        description && (React.createElement(Title, { fontType: "Caption", color: "INK_400", lang: lang }, description)))) : (React.createElement(React.Fragment, null,
                        React.createElement(LoadingSpinner, null),
                        loadingText && (React.createElement(Title, { fontType: "Body", color: "PRIMARY_600", lang: lang }, loadingText)))))),
                imageSource && !isError && (React.createElement(CrossIconWrapper, { onClick: onCrossIconClick },
                    React.createElement(CrossIconInnerWrapper, null,
                        React.createElement(Tooltip.Primary, { lang: lang, text: tooltipMessage, direction: "TOP_CENTER" },
                            React.createElement(Cross, { size: "LARGE" }))))),
                isError && (React.createElement(ExclamationMarkIconWrapper, { onClick: onCrossIconClick },
                    React.createElement(CrossIconInnerWrapper, null,
                        React.createElement(Tooltip.Primary, { lang: lang, text: errorTooltipMessage, direction: "TOP_CENTER" },
                            React.createElement(ExclamationMark, { size: "LARGE", color: "RED_600" }))))))) }, restProps)),
        imageSource && !isLoading && (React.createElement(ImageWrapper, { imageSource: imageSource, borderRadius: borderRadius }))));
};
export default ShowImage;
//# sourceMappingURL=ShowImage.js.map